<template>
  <div>
    <div v-if="isLoading">Loading reports data...</div>

    <div v-else-if="error" class="error-container d-flex justify-center gap5">
      <div class="error-icon" aria-label="Error">&#9888;</div>
      <div class="error-message">
        <strong>Report is not ready.</strong>
      </div>
    </div>

    <div v-else class="container">
      <div class="mb-8" v-if="reviewOptimizationOrderId">
        <router-link :to="`/admin/${user.id}?activeTab=reviewOptimizations`" class="mainColor--text mb-6 d-flex align-center gap10" style="font-size: 1rem;"><i class="material-icons-outlined">arrow_back</i> <b style="font-size: 1rem;">Back to all reports</b></router-link>
        <div class="mainColor--text mb-2" style="font-size: 1rem;">Report ID: <b style="font-size: 1rem;">#{{pageRunId}}</b></div>
        <div class="mainColor--text" style="font-size: 1rem;">Keyword: <b style="font-size: 1rem;">{{keyword}}</b></div>
      </div>
      
      <h1>Existing Page Update Brief - Top Level Page</h1>

      <div class="report-section">
        <h2 class="section-title">Page Feedback</h2>

        <div
          class="keyword-subsection"
        >
          <div class="mb-3 p-3" style="border: 1px solid var(--v-grayColor-base); border-radius: 4px;">
            <v-textarea outlined dense hide-details v-model="top_level_report_feedback" />
          </div>
        </div>

      </div>
      
      <div class="report-section">
        <h2 class="section-title">Page Level SEO Work</h2>

        <div
          class="keyword-subsection"
        >
          <h2 class="sub-section-title mb-4">
            New paragraphs
          </h2>

          <v-btn color="green" class="mb-3" @click="outline_hierarchy = [{'h2': {'text': '', 'builtin': [], 'lsi': []}, 'h3': []}, ...outline_hierarchy]" outlined small>Add H2</v-btn>

          <template v-for="(item, index) in outline_hierarchy || []">

            <div :key="`${index}-item`" class="mb-3 p-3" style="border: 1px solid var(--v-grayColor-base); border-radius: 4px;">
              <div class="d-flex align-center gap5 mb-2">
                <b style="width:30px;">H2:</b> <v-text-field outlined dense hide-details v-model="item.h2.text" /> <v-btn @click="outline_hierarchy = outline_hierarchy.filter((_, i) => i !== index)" outlined small color="red">Remove H2 and children</v-btn>
              </div>
              <div class="pl-8">
                <div v-for="(h3, h3_index) in item.h3" :key="h3_index" class="d-flex align-center gap5 mb-2">
                  <b style="width:30px;">H3:</b> <v-text-field outlined dense hide-details v-model="h3.text" /> <v-btn @click="outline_hierarchy = outline_hierarchy.map((x, i) => i !== index ? x : {...x, h3: x.h3.filter((_, j) => j !== h3_index)})" outlined small color="red">Remove H3</v-btn>
                </div>
                <v-btn color="orange" @click="outline_hierarchy = outline_hierarchy.map((x, i) => i !== index ? x : {...x, h3: [...x.h3, {'text': '', 'builtin': [], 'lsi': []}]})" outlined small>Add H3</v-btn>
              </div>
            </div>

            <v-btn color="green" class="mb-3" :key="`${index}-add-h2`" @click="outline_hierarchy = outline_hierarchy.toSpliced(index + 1, 0, {'h2': {'text': '', 'builtin': [], 'lsi': []}, 'h3': []})" outlined small>Add H2</v-btn>

          </template>
        </div>

        <div
          class="keyword-subsection"
        >
          <h2 class="sub-section-title mb-4">
            FAQ Suggestions
          </h2>

          <v-btn class="mb-3" color="green" @click="faq_data = {faqs: [{question: '', answer: '', intent: ''}, ...faq_data.faqs]}" outlined small>Add item</v-btn>

          <template v-for="(item, index) in faq_data?.faqs || []">
            <div :key="`${index}-item`" class="mb-3 p-3" style="border: 1px solid var(--v-grayColor-base); border-radius: 4px;">
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Question:</b> <v-text-field outlined dense hide-details v-model="item.question" />
              </div>
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Answer:</b> <v-text-field outlined dense hide-details v-model="item.answer" />
              </div>
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Intent:</b> <v-text-field outlined dense hide-details v-model="item.intent" />
              </div>
              <v-btn @click="faq_data = {faqs: faq_data.faqs.filter((_, i) => i !== index)}" outlined small color="red">Remove</v-btn>
            </div>
            <v-btn :key="`${index}-add-item`" class="mb-3" color="green" @click="faq_data = {faqs: faq_data.faqs.toSpliced(index + 1, 0, {question: '', answer: '', intent: ''})}" outlined small>Add item</v-btn>
          </template>
        </div>
        
      </div>

      <div class="report-section">
        <h2 class="section-title">SEO Word Work</h2>

        <div
          class="keyword-subsection"
        >
          <h2 class="sub-section-title mb-4">
            Recommended Title
          </h2>

          <div>
            <v-btn
              v-if="!recommend_title?.recommendation" class="mb-3" color="green"
              @click="recommend_title = {...recommend_title, recommendation: {'draft_title': '', 'option': '', 'score_gain': '0', 'score_total': 0, 'why': '', 'why_strong': ''}}"
              outlined small
            >Add main recommendation</v-btn>
            <template v-else>
              <div class="mb-1"><b>Main recommended title</b></div>
              <div class="mb-3 p-3" style="border: 1px solid var(--v-grayColor-base); border-radius: 4px;">
                <div class="d-flex align-center gap5">
                  <b style="width:130px;">Title:</b> <v-text-field outlined dense hide-details v-model="recommend_title.recommendation.draft_title" />
                </div>
                <div class="d-flex align-center gap5">
                  <b style="width:130px;">Option (A/B/C):</b> <v-text-field outlined dense hide-details v-model="recommend_title.recommendation.option" />
                </div>
                <div class="d-flex align-center gap5">
                  <b style="width:130px;">Score gain (0-100):</b> <v-text-field outlined dense hide-details v-model="recommend_title.recommendation.score_gain" />
                </div>
                <div class="d-flex align-center gap5">
                  <b style="width:130px;">Score total (0-100):</b> <v-text-field outlined dense hide-details v-model="recommend_title.recommendation.score_total" />
                </div>
                <div class="d-flex align-center gap5">
                  <b style="width:130px;">Why:</b> <v-text-field outlined dense hide-details v-model="recommend_title.recommendation.why" />
                </div>
                <div class="d-flex align-center gap5">
                  <b style="width:130px;">Why strong:</b> <v-text-field outlined dense hide-details v-model="recommend_title.recommendation.why_strong" />
                </div>
                <v-btn v-if="(recommend_title?.ranked_alternatives || []).length < 2" @click="recommend_title = {...recommend_title?.ranked_alternatives?{ranked_alternatives: recommend_title?.ranked_alternatives}:{}}" outlined small color="red">Remove</v-btn>
              </div>
            </template>
          </div>

          <template v-if="(recommend_title?.ranked_alternatives || []).length">
            <div class="mb-1"><b>Alternative titles (max 2)</b></div>
            <template v-for="(item, index) in recommend_title?.ranked_alternatives || []">
              <div :key="`${index}-item`" class="mb-3 p-3" style="border: 1px solid var(--v-grayColor-base); border-radius: 4px;">
                <div class="d-flex align-center gap5">
                  <b style="width:130px;">Title:</b> <v-text-field outlined dense hide-details v-model="item.draft_title" />
                </div>
                <div class="d-flex align-center gap5">
                  <b style="width:130px;">Option (A/B/C):</b> <v-text-field outlined dense hide-details v-model="item.option" />
                </div>
                <div class="d-flex align-center gap5">
                  <b style="width:130px;">Score gain (0-100):</b> <v-text-field outlined dense hide-details v-model="item.score_gain" />
                </div>
                <div class="d-flex align-center gap5">
                  <b style="width:130px;">Score total (0-100):</b> <v-text-field outlined dense hide-details v-model="item.score_total" />
                </div>
                <div class="d-flex align-center gap5">
                  <b style="width:130px;">Why:</b> <v-text-field outlined dense hide-details v-model="item.why" />
                </div>
                <div class="d-flex align-center gap5">
                  <b style="width:130px;">Why strong:</b> <v-text-field outlined dense hide-details v-model="item.why_strong" />
                </div>
                <v-btn @click="recommend_title = {...recommend_title, ranked_alternatives: recommend_title.ranked_alternatives.filter((_, i) => i !== index)}" outlined small color="red">Remove</v-btn>
              </div>
            </template>
          </template>
          <v-btn v-if="(recommend_title?.ranked_alternatives || []).length < 2" @click="recommend_title = {...recommend_title, ranked_alternatives: [...(recommend_title?.ranked_alternatives || []), {'draft_title': '', 'option': '', 'score_gain': '0', 'score_total': 0, 'why': '', 'why_strong': ''}]}" outlined small color="green">Add alternative</v-btn>
        </div>

        <div
          class="keyword-subsection"
        >
          <h2 class="sub-section-title mb-4">
            Title keyword suggestions (Add)
          </h2>

          <v-btn class="mb-3" color="green" @click="keywordSuggestions = {...(keywordSuggestions || {}), add: {...(keywordSuggestions?.add || {}), title: [{'current': 0, 'keyword': '', 'target': 0, 'targetMax': 0, 'targetMin': 0}, ...(keywordSuggestions?.add?.title || [])]}}" outlined small>Add item</v-btn>

          <template v-for="(item, index) in keywordSuggestions?.add?.title || []">
            <div :key="`${index}-item`" class="mb-3 p-3" style="border: 1px solid var(--v-grayColor-base); border-radius: 4px;">
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Keyword:</b> <v-text-field outlined dense hide-details v-model="item.keyword" />
              </div>
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Current (count):</b> <v-text-field outlined dense hide-details number v-model="item.current" />
              </div>
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Target (count):</b> <v-text-field outlined dense hide-details number v-model="item.target" />
              </div>
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Target min (count):</b> <v-text-field outlined dense hide-details number v-model="item.targetMin" />
              </div>
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Target max (count):</b> <v-text-field outlined dense hide-details number v-model="item.targetMax" />
              </div>
              <v-btn @click="keywordSuggestions = {...(keywordSuggestions || {}), add: {...(keywordSuggestions?.add || {}), title: (keywordSuggestions?.add?.title || []).filter((_, i) => i !== index)}}" outlined small color="red">Remove</v-btn>
            </div>
            <v-btn :key="`${index}-add-item`" class="mb-3" color="green" @click="keywordSuggestions = {...(keywordSuggestions || {}), add: {...(keywordSuggestions?.add || {}), title: (keywordSuggestions?.add?.title || []).toSpliced(index + 1, 0, {'current': 0, 'keyword': '', 'target': 0, 'targetMax': 0, 'targetMin': 0})}}" outlined small>Add item</v-btn>
          </template>
        </div>

        <div
          class="keyword-subsection"
        >
          <h2 class="sub-section-title mb-4">
            Page title keyword suggestions (Add)
          </h2>

          <v-btn class="mb-3" color="green" @click="keywordSuggestions = {...(keywordSuggestions || {}), add: {...(keywordSuggestions?.add || {}), pageTitle: [{'current': 0, 'keyword': '', 'target': 0, 'targetMax': 0, 'targetMin': 0}, ...(keywordSuggestions?.add?.pageTitle || [])]}}" outlined small>Add item</v-btn>

          <template v-for="(item, index) in keywordSuggestions?.add?.pageTitle || []">
            <div :key="`${index}-item`" class="mb-3 p-3" style="border: 1px solid var(--v-grayColor-base); border-radius: 4px;">
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Keyword:</b> <v-text-field outlined dense hide-details v-model="item.keyword" />
              </div>
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Current (count):</b> <v-text-field outlined dense hide-details number v-model="item.current" />
              </div>
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Target (count):</b> <v-text-field outlined dense hide-details number v-model="item.target" />
              </div>
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Target min (count):</b> <v-text-field outlined dense hide-details number v-model="item.targetMin" />
              </div>
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Target max (count):</b> <v-text-field outlined dense hide-details number v-model="item.targetMax" />
              </div>
              <v-btn @click="keywordSuggestions = {...(keywordSuggestions || {}), add: {...(keywordSuggestions?.add || {}), pageTitle: (keywordSuggestions?.add?.pageTitle || []).filter((_, i) => i !== index)}}" outlined small color="red">Remove</v-btn>
            </div>
            <v-btn :key="`${index}-add-item`" class="mb-3" color="green" @click="keywordSuggestions = {...(keywordSuggestions || {}), add: {...(keywordSuggestions?.add || {}), pageTitle: (keywordSuggestions?.add?.pageTitle || []).toSpliced(index + 1, 0, {'current': 0, 'keyword': '', 'target': 0, 'targetMax': 0, 'targetMin': 0})}}" outlined small>Add item</v-btn>
          </template>
        </div>

        <div
          class="keyword-subsection"
        >
          <h2 class="sub-section-title mb-4">
            Title reduce/remove (only if overoptimization checked)
          </h2>

          <v-btn class="mb-3" color="green" @click="titleReduceAll = [{'current': 0, 'keyword': '', 'target': 0, 'targetMax': 0, 'targetMin': 0}, ...(titleReduceAll || [])]" outlined small>Add item</v-btn>

          <template v-for="(item, index) in titleReduceAll || []">
            <div :key="`${index}-item`" class="mb-3 p-3" style="border: 1px solid var(--v-grayColor-base); border-radius: 4px;">
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Keyword:</b> <v-text-field outlined dense hide-details v-model="item.keyword" />
              </div>
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Current (count):</b> <v-text-field outlined dense hide-details number v-model="item.current" />
              </div>
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Target (count):</b> <v-text-field outlined dense hide-details number v-model="item.target" />
              </div>
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Target min (count):</b> <v-text-field outlined dense hide-details number v-model="item.targetMin" />
              </div>
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Target max (count):</b> <v-text-field outlined dense hide-details number v-model="item.targetMax" />
              </div>
              <v-btn @click="titleReduceAll = titleReduceAll.filter((_, i) => i !== index)" outlined small color="red">Remove</v-btn>
            </div>
            <v-btn :key="`${index}-add-item`" class="mb-3" color="green" @click="titleReduceAll = titleReduceAll.toSpliced(index + 1, 0, {'current': 0, 'keyword': '', 'target': 0, 'targetMax': 0, 'targetMin': 0})" outlined small>Add item</v-btn>
          </template>
        </div>

        <div
          class="keyword-subsection"
        >
          <h2 class="sub-section-title mb-4">
            Subheadings increase suggestions
          </h2>

          <v-btn class="mb-3" color="green" @click="subHeadingsAddAll = [{'current': 0, 'keyword': '', 'target': 0, 'targetMax': 0, 'targetMin': 0}, ...(subHeadingsAddAll || [])]" outlined small>Add item</v-btn>

          <template v-for="(item, index) in subHeadingsAddAll || []">
            <div :key="`${index}-item`" class="mb-3 p-3" style="border: 1px solid var(--v-grayColor-base); border-radius: 4px;">
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Keyword:</b> <v-text-field outlined dense hide-details v-model="item.keyword" />
              </div>
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Current (count):</b> <v-text-field outlined dense hide-details number v-model="item.current" />
              </div>
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Target (count):</b> <v-text-field outlined dense hide-details number v-model="item.target" />
              </div>
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Target min (count):</b> <v-text-field outlined dense hide-details number v-model="item.targetMin" />
              </div>
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Target max (count):</b> <v-text-field outlined dense hide-details number v-model="item.targetMax" />
              </div>
              <v-btn @click="subHeadingsAddAll = subHeadingsAddAll.filter((_, i) => i !== index)" outlined small color="red">Remove</v-btn>
            </div>
            <v-btn :key="`${index}-add-item`" class="mb-3" color="green" @click="subHeadingsAddAll = subHeadingsAddAll.toSpliced(index + 1, 0, {'current': 0, 'keyword': '', 'target': 0, 'targetMax': 0, 'targetMin': 0})" outlined small>Add item</v-btn>
          </template>
        </div>

        <div
          class="keyword-subsection"
        >
          <h2 class="sub-section-title mb-4">
            Subheadings decrease suggestions (only if overoptimization checked)
          </h2>

          <v-btn class="mb-3" color="green" @click="subHeadingsReduceAll = [{'current': 0, 'keyword': '', 'target': 0, 'targetMax': 0, 'targetMin': 0}, ...(subHeadingsReduceAll || [])]" outlined small>Add item</v-btn>

          <template v-for="(item, index) in subHeadingsReduceAll || []">
            <div :key="`${index}-item`" class="mb-3 p-3" style="border: 1px solid var(--v-grayColor-base); border-radius: 4px;">
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Keyword:</b> <v-text-field outlined dense hide-details v-model="item.keyword" />
              </div>
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Current (count):</b> <v-text-field outlined dense hide-details number v-model="item.current" />
              </div>
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Target (count):</b> <v-text-field outlined dense hide-details number v-model="item.target" />
              </div>
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Target min (count):</b> <v-text-field outlined dense hide-details number v-model="item.targetMin" />
              </div>
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Target max (count):</b> <v-text-field outlined dense hide-details number v-model="item.targetMax" />
              </div>
              <v-btn @click="subHeadingsReduceAll = subHeadingsReduceAll.filter((_, i) => i !== index)" outlined small color="red">Remove</v-btn>
            </div>
            <v-btn :key="`${index}-add-item`" class="mb-3" color="green" @click="subHeadingsReduceAll = subHeadingsReduceAll.toSpliced(index + 1, 0, {'current': 0, 'keyword': '', 'target': 0, 'targetMax': 0, 'targetMin': 0})" outlined small>Add item</v-btn>
          </template>
        </div>

        <div
          class="keyword-subsection"
        >
          <h2 class="sub-section-title mb-4">
            Paragraphs increase suggestions
          </h2>

          <v-btn class="mb-3" color="green" @click="pAddAll = [{'current': 0, 'keyword': '', 'target': 0, 'targetMax': 0, 'targetMin': 0}, ...(pAddAll || [])]" outlined small>Add item</v-btn>

          <template v-for="(item, index) in pAddAll || []">
            <div :key="`${index}-item`" class="mb-3 p-3" style="border: 1px solid var(--v-grayColor-base); border-radius: 4px;">
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Keyword:</b> <v-text-field outlined dense hide-details v-model="item.keyword" />
              </div>
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Current (count):</b> <v-text-field outlined dense hide-details number v-model="item.current" />
              </div>
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Target (count):</b> <v-text-field outlined dense hide-details number v-model="item.target" />
              </div>
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Target min (count):</b> <v-text-field outlined dense hide-details number v-model="item.targetMin" />
              </div>
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Target max (count):</b> <v-text-field outlined dense hide-details number v-model="item.targetMax" />
              </div>
              <v-btn @click="pAddAll = pAddAll.filter((_, i) => i !== index)" outlined small color="red">Remove</v-btn>
            </div>
            <v-btn :key="`${index}-add-item`" class="mb-3" color="green" @click="pAddAll = pAddAll.toSpliced(index + 1, 0, {'current': 0, 'keyword': '', 'target': 0, 'targetMax': 0, 'targetMin': 0})" outlined small>Add item</v-btn>
          </template>
        </div>

        <div
          class="keyword-subsection"
        >
          <h2 class="sub-section-title mb-4">
            Paragraphs decrease suggestions (only if overoptimization checked)
          </h2>

          <v-btn class="mb-3" color="green" @click="pReduceAll = [{'current': 0, 'keyword': '', 'target': 0, 'targetMax': 0, 'targetMin': 0}, ...(pReduceAll || [])]" outlined small>Add item</v-btn>

          <template v-for="(item, index) in pReduceAll || []">
            <div :key="`${index}-item`" class="mb-3 p-3" style="border: 1px solid var(--v-grayColor-base); border-radius: 4px;">
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Keyword:</b> <v-text-field outlined dense hide-details v-model="item.keyword" />
              </div>
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Current (count):</b> <v-text-field outlined dense hide-details number v-model="item.current" />
              </div>
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Target (count):</b> <v-text-field outlined dense hide-details number v-model="item.target" />
              </div>
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Target min (count):</b> <v-text-field outlined dense hide-details number v-model="item.targetMin" />
              </div>
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Target max (count):</b> <v-text-field outlined dense hide-details number v-model="item.targetMax" />
              </div>
              <v-btn @click="pReduceAll = pReduceAll.filter((_, i) => i !== index)" outlined small color="red">Remove</v-btn>
            </div>
            <v-btn :key="`${index}-add-item`" class="mb-3" color="green" @click="pReduceAll = pReduceAll.toSpliced(index + 1, 0, {'current': 0, 'keyword': '', 'target': 0, 'targetMax': 0, 'targetMin': 0})" outlined small>Add item</v-btn>
          </template>
        </div>

        <div
          class="keyword-subsection"
        >
          <h2 class="sub-section-title mb-4">
            Related keywords (from old search results)
          </h2>

          <v-btn class="mb-3" color="green" @click="old_related_searches = [{'block_position': 1, 'link': '', 'query': '', 'serpapi_link': ''}, ...(old_related_searches || [])]" outlined small>Add item</v-btn>

          <template v-for="(item, index) in old_related_searches || []">
            <div :key="`${index}-item`" class="mb-3 p-3" style="border: 1px solid var(--v-grayColor-base); border-radius: 4px;">
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Query:</b> <v-text-field outlined dense hide-details v-model="item.query" />
              </div>
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Link:</b> <v-text-field outlined dense hide-details v-model="item.link" />
              </div>
              <div class="d-flex align-center gap5">
                <b style="width:100px;">SERP API Link:</b> <v-text-field outlined dense hide-details v-model="item.serpapi_link" />
              </div>
              <v-btn @click="old_related_searches = old_related_searches.filter((_, i) => i !== index)" outlined small color="red">Remove</v-btn>
            </div>
            <v-btn :key="`${index}-add-item`" class="mb-3" color="green" @click="old_related_searches = old_related_searches.toSpliced(index + 1, 0, {'block_position': 1, 'link': '', 'query': '', 'serpapi_link': ''})" outlined small>Add item</v-btn>
          </template>
        </div>

        <div
          class="keyword-subsection"
        >
          <h2 class="sub-section-title mb-4">
            Related keywords (from new search results)
          </h2>

          <v-btn class="mb-3" color="green" @click="new_related_searches = [{'block_position': 1, 'link': '', 'query': '', 'serpapi_link': ''}, ...(new_related_searches || [])]" outlined small>Add item</v-btn>

          <template v-for="(item, index) in new_related_searches || []">
            <div :key="`${index}-item`" class="mb-3 p-3" style="border: 1px solid var(--v-grayColor-base); border-radius: 4px;">
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Query:</b> <v-text-field outlined dense hide-details v-model="item.query" />
              </div>
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Link:</b> <v-text-field outlined dense hide-details v-model="item.link" />
              </div>
              <div class="d-flex align-center gap5">
                <b style="width:100px;">SERP API Link:</b> <v-text-field outlined dense hide-details v-model="item.serpapi_link" />
              </div>
              <v-btn @click="new_related_searches = new_related_searches.filter((_, i) => i !== index)" outlined small color="red">Remove</v-btn>
            </div>
            <v-btn :key="`${index}-add-item`" class="mb-3" color="green" @click="new_related_searches = new_related_searches.toSpliced(index + 1, 0, {'block_position': 1, 'link': '', 'query': '', 'serpapi_link': ''})" outlined small>Add item</v-btn>
          </template>
        </div>

      </div>

      <div class="report-section">
        <h2 class="section-title">Page Schema</h2>

        <div
          class="keyword-subsection"
        >

          <v-btn class="mb-3" color="green" @click="schema_data = [{'count': 1, 'type': ''}, ...(schema_data || [])]" outlined small>Add item</v-btn>

          <template v-for="(item, index) in schema_data || []">
            <div :key="`${index}-item`" class="mb-3 p-3" style="border: 1px solid var(--v-grayColor-base); border-radius: 4px;">
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Schema type:</b> <v-text-field outlined dense hide-details v-model="item.type" />
              </div>
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Count:</b> <v-text-field outlined dense hide-details number v-model="item.count" />
              </div>
              <v-btn @click="schema_data = schema_data.filter((_, i) => i !== index)" outlined small color="red">Remove</v-btn>
            </div>
            <v-btn :key="`${index}-add-item`" class="mb-3" color="green" @click="schema_data = schema_data.toSpliced(index + 1, 0, {'count': 1, 'type': ''})" outlined small>Add item</v-btn>
          </template>
        </div>

      </div>

      <div class="report-section">
        <h2 class="section-title">EEAT Signals</h2>

        <div
          class="keyword-subsection"
        >

          <v-btn class="mb-3" color="green" @click="eatSchemaTypes = [{'count': 1, 'signal': ''}, ...(eatSchemaTypes || [])]" outlined small>Add item</v-btn>

          <template v-for="(item, index) in eatSchemaTypes || []">
            <div :key="`${index}-item`" class="mb-3 p-3" style="border: 1px solid var(--v-grayColor-base); border-radius: 4px;">
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Signal type:</b> <v-text-field outlined dense hide-details v-model="item.signal" />
              </div>
              <div class="d-flex align-center gap5">
                <b style="width:100px;">Count:</b> <v-text-field outlined dense hide-details number v-model="item.count" />
              </div>
              <v-btn @click="eatSchemaTypes = eatSchemaTypes.filter((_, i) => i !== index)" outlined small color="red">Remove</v-btn>
            </div>
            <v-btn :key="`${index}-add-item`" class="mb-3" color="green" @click="eatSchemaTypes = eatSchemaTypes.toSpliced(index + 1, 0, {'count': 1, 'signal': ''})" outlined small>Add item</v-btn>
          </template>
        </div>

      </div>

      <!-- Save Button -->
      <hr />
      
      <div class="mt-4 d-flex is-justify-content-flex-end">
        <v-btn
          color="profileAnchorColor"
          class="button-with-icon whiteColor--text text-none ml-1"
          @click="saveChanges"
          :disabled="isSaving || isLoading"
        >
          <span>{{ isSaving ? "Saving..." : "Save Changes" }}</span>
        </v-btn>
        <span v-if="saveStatus" class="ml-3">{{ saveStatus }}</span>
      </div>
    </div>
  </div>
</template>

<script>

export default {

  data() {
    return {
      reportData: null,

      outline_hierarchy: null,
      active_outline_hierarchy_path : null,  // 'outline_hierarchy' or 'result_outline_hierarchy'

      top_level_report_feedback: null,
      
      faq_data: null,

      recommend_title: null,

      keywordSuggestions: null,

      titleReduceAll: null,

      subHeadingsAddAll: null,

      subHeadingsReduceAll: null,

      pAddAll: null,

      pReduceAll: null,

      old_related_searches: null,

      new_related_searches: null,

      schema_data: null,

      eatSchemaTypes: null,

      isLoading: false,
      error: null,

      isSaving: false,
      saveStatus: "",
      saveTimeout: null,

      siloKeywordId: null,
      reviewOptimizationOrderId: null,
      pageRunId: null,
      keyword: null
    };
  },

  created() {
    this.siloKeywordId = this.$route.params.siloKeywordId;
    this.reviewOptimizationOrderId = this.$route.params.reviewOptimizationOrderId;
    if (this.reviewOptimizationOrderId) {
      this.pageRunId = this.$route.params.pageRunId;
      this.keyword = this.$route.params.keyword;
    }
  },

  computed: {
    user() {
      return this.$store.state.user
    },

    storeReportData() {
      return this.$store.state.reportsData;
    },
    storeLoading() {
      return this.$store.state.reportsLoading;
    },
    storeError() {
      return this.$store.state.reportsError;
    },
    updatedData() {
      const result = {
        ...this.reportData,
      };

      if (this.active_outline_hierarchy_path === 'result_outline_hierarchy') {
        result['outline_data']['result']['outline']['hierarchy'] = this.outline_hierarchy
      } else if (this.active_outline_hierarchy_path === 'outline_hierarchy') {
        result['outline_data']['outline']['hierarchy'] = this.outline_hierarchy
      }

      result['top_level_report_feedback'] = this.top_level_report_feedback
      
      result['faq_data'] = this.faq_data

      result['recommend_title'] = this.recommend_title

      result['keywordSuggestions'] = this.keywordSuggestions

      result['titleReduceAll'] = this.titleReduceAll

      result['subHeadingsAddAll'] = this.subHeadingsAddAll

      result['subHeadingsReduceAll'] = this.subHeadingsReduceAll

      result['pAddAll'] = this.pAddAll

      result['pReduceAll'] = this.pReduceAll

      result['old_related_searches'] = this.old_related_searches

      result['new_related_searches'] = this.new_related_searches

      result['schema_data'] = this.schema_data

      result['eatSchemaTypes'] = this.eatSchemaTypes

      return result
    }
  },

  watch: {
    storeLoading(newVal) {
      this.isLoading = newVal;
    },
    storeError(newVal) {
      if (newVal) {
        this.error = newVal;
      }
    }
  },

  methods: {

    async loadReportsData() {
      this.isLoading = true;
      this.error = null;

      try {
        await this.$store.dispatch("loadReportsData", {
          ...this.siloKeywordId?{siloKeywordId: this.siloKeywordId}:{},
          ...this.reviewOptimizationOrderId?{reviewOptimizationOrderId: this.reviewOptimizationOrderId}:{}
        });

        const report = this.$store.state.reportsData;
        this.reportData = report;

        // See silo-reports.html template. It checks if data exists inside a 'result' attribute
        // this is why we use active_outline_hierarchy_path to know what fields we let the admin edit and where to store edited data
        if (this.reportData?.outline_data?.outline?.hierarchy) {
          this.outline_hierarchy = this.reportData?.outline_data?.outline?.hierarchy
          this.active_outline_hierarchy_path = 'outline_hierarchy'
        } else if (this.reportData?.outline_data?.result?.outline?.hierarchy) {
          this.outline_hierarchy = this.reportData?.outline_data?.result?.outline?.hierarchy
          this.active_outline_hierarchy_path = 'result_outline_hierarchy'
        }

        this.top_level_report_feedback = this.reportData?.top_level_report_feedback
        
        this.faq_data = this.reportData?.faq_data

        this.recommend_title = this.reportData?.recommend_title

        this.keywordSuggestions = this.reportData?.keywordSuggestions

        this.titleReduceAll = this.reportData?.titleReduceAll

        this.subHeadingsAddAll = this.reportData?.subHeadingsAddAll

        this.subHeadingsReduceAll = this.reportData?.subHeadingsReduceAll

        this.pAddAll = this.reportData?.pAddAll

        this.pReduceAll = this.reportData?.pReduceAll

        this.old_related_searches = this.reportData?.old_related_searches

        this.new_related_searches = this.reportData?.new_related_searches

        this.schema_data = this.reportData?.schema_data

        this.eatSchemaTypes = this.reportData?.eatSchemaTypes

        if (!report || typeof report !== "object" || Array.isArray(report)) {
          this.error = "Report is not ready.";
          return;
        }

        if (report.status === "FAILURE") {
          this.error = report.msg || "Report is not ready.";
          return;
        }

        const headerImage =
          report.headerImage || this.$store.state.reportsHeaderImage;

        // Case 1: backend sent error object instead of base64
        // headerImage = { msg: "Invalid Report Type.", status: "FAILURE" }
        if (
          headerImage &&
          typeof headerImage === "object" &&
          headerImage.status === "FAILURE"
        ) {
          this.error = headerImage.msg || "Invalid Report Type.";
          return;
        }

      } catch (err) {
        console.log(err, "ASADSAFD");
        this.error =
          this.$store.state.reportsError ||
          "Failed to load data. Please try again.";
      } finally {
        this.isLoading = false;
      }
    },

    debouncedSave() {
      if (this.saveTimeout) {
        clearTimeout(this.saveTimeout);
      }
      this.saveTimeout = setTimeout(() => {
        this.saveChanges();
      }, 2000);
    },

    async saveChanges() {
      if (this.isSaving) {
        return;
      }

      this.isSaving = true;
      this.saveStatus = "Saving...";

      try {

        const payload = {
          ...this.siloKeywordId?{siloKeywordId: this.siloKeywordId}:{},
          ...this.reviewOptimizationOrderId?{reviewOptimizationOrderId: this.reviewOptimizationOrderId}:{},
          data: this.updatedData
        };

        await this.$store.dispatch("saveEditedData", payload);

        this.saveStatus = "Saved ✓";
        setTimeout(() => {
          this.saveStatus = "";
        }, 3000);
      } catch (error) {
        this.saveStatus = "Save failed ✗";

        if (this.$store.state.reportsError) {
          this.error = this.$store.state.reportsError;
        }

        setTimeout(() => {
          this.saveStatus = "";
        }, 5000);
      } finally {
        this.isSaving = false;
      }
    }
  },

  mounted() {
    this.loadReportsData();
  },

  beforeDestroy() {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }
  }
};
</script>

<style scoped>
.container {
  width: 800px;
  margin: auto;
  h1 {
    font-family: Mulish;
    font-weight: 700;
    font-size: 35px;
    line-height: 130%;
    color: #014751;
  }
}

.report-section {
  margin-bottom: 40px;
}

.section-header {
  font-family: Mulish;
  font-weight: 700;
  font-size: 16px;
  color: #666;
  margin-bottom: 15px;
  padding: 10px 0;
  color: #014751;
}

.section-title {
  font-family: Mulish;
  font-weight: 700;
  font-size: 24px;
  margin: 20px 0 15px 0;
  color: #014751;
}
.sub-section-title{
  font-family: Mulish;
  font-weight: 700;
  font-size: 18px;
  line-height: 130%;
  color: #014751;
  margin-left: 10px;
  }

.keyword-subsection {
  margin-bottom: 30px;
}


</style>
